import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { publicApi } from "@/lib/api";

/**
 * Site-wide configuration for the public website.
 *
 * Contact details, social links and the page-key to path map come from the
 * database so PIRAC can change a phone number without a deployment. The values
 * below are the fallback used while the request is in flight, or if the API is
 * briefly unreachable — a footer with no phone number is a worse failure than a
 * slightly stale one.
 */

const FALLBACK = {
  site: {
    name: "PIRAC",
    tagline: "Property Investment Research and Advisory Centre",
    contactEmail: "property@consultant.com",
    contactPhone: "+65 8484 1000",
    whatsappNumber: "6584841000",
    officeAddress: "",
    socialLinks: {},
  },
  pages: [],
  forms: { honeypotField: "company_website", requireCaptcha: false },
};

const SiteContext = createContext(FALLBACK);

export function SiteProvider({ children }) {
  const { data } = useQuery({
    queryKey: ["public", "bootstrap"],
    queryFn: publicApi.bootstrap,
    staleTime: 10 * 60 * 1000,
    // Never surface a failure here: the site must render regardless.
    retry: 1,
    placeholderData: FALLBACK,
  });

  const value = useMemo(() => {
    const resolved = data || FALLBACK;
    return {
      site: { ...FALLBACK.site, ...(resolved.site || {}) },
      pages: resolved.pages || [],
      forms: { ...FALLBACK.forms, ...(resolved.forms || {}) },
      /** Current path for a page key, honouring any custom slug. */
      pathFor: (key) => {
        const page = (resolved.pages || []).find((p) => p.key === key);
        return page ? page.path : "/";
      },
    };
  }, [data]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  return useContext(SiteContext);
}
